// src/pages/ServiceManagement/CreateServiceDialog.jsx
import React, { useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Box,
  CircularProgress,
  Alert
} from '@mui/material';
import { useSnackbar } from 'notistack';
import { servicesApi } from '../../services/apiClient';

const CreateServiceDialog = ({ open, onClose, onSuccess }) => {
  const { enqueueSnackbar } = useSnackbar();
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    friendlyName: '',
    description: '',
    logo: ''
  });
  const [errors, setErrors] = useState({});

  const handleChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    if (errors[field]) {
      setErrors(prev => ({ ...prev, [field]: undefined }));
    }
  };

  const validate = () => {
    const newErrors = {};

    if (!formData.name || formData.name.trim().length < 2) {
      newErrors.name = 'Service name must be at least 2 characters';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async () => {
    if (!validate()) return;

    try {
      setLoading(true);
      const response = await servicesApi.create(formData);

      if (response.data.success) {
        onSuccess(response.data.data);
        setFormData({ name: '', friendlyName: '', description: '', logo: '' });
      }
    } catch (error) {
      console.error('Error creating service:', error);
      enqueueSnackbar(
        error.response?.data?.error || 'Failed to create service',
        { variant: 'error' }
      );
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    if (!loading) {
      setFormData({ name: '', friendlyName: '', description: '', logo: '' });
      setErrors({});
      onClose();
    }
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
      <DialogTitle>Create New Service</DialogTitle>
      <DialogContent>
        <Box sx={{ pt: 2, display: 'flex', flexDirection: 'column', gap: 2 }}>
          <Alert severity="info">
            Create a new service to manage its versions and update paths.
          </Alert>

          <TextField
            fullWidth
            label="Service Name"
            value={formData.name}
            onChange={(e) => handleChange('name', e.target.value)}
            error={!!errors.name}
            helperText={errors.name || 'Enter a unique service name (technical identifier)'}
            disabled={loading}
            required
          />

          <TextField
            fullWidth
            label="Friendly Name"
            value={formData.friendlyName}
            onChange={(e) => handleChange('friendlyName', e.target.value)}
            helperText="Display name for the service (optional)"
            disabled={loading}
          />

          <TextField
            fullWidth
            label="Description"
            value={formData.description}
            onChange={(e) => handleChange('description', e.target.value)}
            helperText="Brief description of the service"
            disabled={loading}
            multiline
            rows={3}
          />

          <TextField
            fullWidth
            label="Logo URL"
            value={formData.logo}
            onChange={(e) => handleChange('logo', e.target.value)}
            helperText="Optional logo image URL"
            disabled={loading}
          />
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose} disabled={loading}>
          Cancel
        </Button>
        <Button
          variant="contained"
          onClick={handleSubmit}
          disabled={loading}
          startIcon={loading && <CircularProgress size={20} />}
        >
          Create
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default CreateServiceDialog;
